import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import {
  SegmentBuilderDrawer,
  type SegmentLoadState,
} from '../components/segment-builder-drawer';
import { SegmentsOverview } from '../components/segments-overview';
import { useGetSegmentById } from '../hooks/use-segments';
import { isSegmentNotFoundError } from '../utils/segment-api-errors';

export const SegmentsPage = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clearSearchRequest, setClearSearchRequest] = useState(0);
  const isNew = searchParams.has('new');
  const editId = searchParams.get('edit');
  const drawerOpen = isNew || Boolean(editId);
  const mode = editId ? 'edit' : 'create';

  const {
    data: editSegment,
    isLoading: isEditSegmentLoading,
    isError: isEditSegmentError,
    refetch: refetchEditSegment,
    error: editSegmentError,
  } = useGetSegmentById(editId ?? '', {
    enabled: Boolean(editId),
  });

  useEffect(() => {
    if (!editId || !isSegmentNotFoundError(editSegmentError)) return;
    toast({
      variant: 'destructive',
      title: 'This segment is no longer available',
    });
    setSearchParams({}, { replace: true });
  }, [editId, editSegmentError, setSearchParams, toast]);

  const segmentLoadState = useMemo<SegmentLoadState>(() => {
    if (mode === 'create') return 'ready';
    if (isEditSegmentLoading) return 'loading';
    if (isSegmentNotFoundError(editSegmentError)) return 'not_found';
    if (isEditSegmentError) return 'error';
    if (!editSegment) return 'not_found';
    return 'ready';
  }, [editSegment, editSegmentError, isEditSegmentError, isEditSegmentLoading, mode]);

  const openCreate = () => setSearchParams({ new: '' }, { replace: true });
  const openEdit = (id: string) => setSearchParams({ edit: id }, { replace: true });
  const closeDrawer = () => setSearchParams({}, { replace: true });

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full max-w-full flex-col overflow-x-hidden">
      <SegmentsOverview
        onCreate={openCreate}
        onEdit={openEdit}
        clearSearchRequest={clearSearchRequest}
      />
      <SegmentBuilderDrawer
        open={drawerOpen}
        mode={mode}
        segmentId={editId}
        segment={editSegment}
        segmentLoadState={segmentLoadState}
        onRetryLoad={() => void refetchEditSegment()}
        onOpenChange={(open) => {
          if (!open) closeDrawer();
        }}
        onSaved={() => {
          if (mode === 'create') setClearSearchRequest((request) => request + 1);
          closeDrawer();
        }}
      />
    </div>
  );
};
